import { RefObject, useCallback, useEffect, useRef } from 'react'
import { FormikProps } from 'formik'
import { debounce } from 'lodash'
import { PaymentPayee, useCyberStore, checkIsGameKey, getVisibleTspFields } from '@entities/tsp'
import { getPcekServiceParamsByTspUrl, PaymentFormValues, PaymentMethod } from '@entities/payment'
import { useAuthDataStore, useBindingsStore } from '@entities/user'
import { CommissionDataByServiceIdType } from '@entities/denomination'
import { isCardMatch } from '@entities/bank'
import { useToast } from '@shared/lib/toast'
import { MESSAGE_COMMISSION } from '@shared/config'
import { PayeeBindingTypesEnum } from '@root/api/ump-catalog-manager'
import { Amount, Binding as EWalletBinding, TransferTermsRs } from '@root/api/ump-ewallet'
import { Binding } from '@root/api/ump-plat-sdk-gateway'
import { Currency } from '@root/types/generated'
import { getActualServiceForCommision } from '@entities/voucher/lib/getActualServiceForCommision'
import { useGetPaymentTerms } from './useGetPaymentTerms'
import { parseAmountInfo } from '../lib/parseAmountInfo'
import { CARD_NUMBER_LENGTH, MOCK_PAN_NUMBER } from '../config/form'
import { getCardAdditionalAmount } from '../lib/getCardAdditionalAmount'

interface useGetCommissionsArgs {
  isVouchers: boolean
  payee: PaymentPayee
  paymentFormRef: RefObject<FormikProps<PaymentFormValues>>
  commissionData: CommissionDataByServiceIdType
}

export const useGetCommissions = ({ isVouchers, payee, paymentFormRef, commissionData }: useGetCommissionsArgs) => {
  const { isPromocodeActivated, paymentMethod, setAmountInfo, setPcekMdOrder, activeDenomination } = useCyberStore()
  const { bindings } = useBindingsStore()
  const { isAuth, operatorMTS } = useAuthDataStore()

  const { toast } = useToast()

  const { getPaymentTerms, isLoading: isGetPaymentTermsLoading, cancelGetPaymentTermsRequest } = useGetPaymentTerms()

  const isSteam = payee?.features?.tsp_url === 'steam'

  // реф нужен для того, чтобы значение не замыкалось в useCallback
  const isPromocodeActivatedRef = useRef(isPromocodeActivated)

  useEffect(() => {
    isPromocodeActivatedRef.current = isPromocodeActivated
  }, [isPromocodeActivated])

  const updateAmountInfo = (base: string, total: string, fee: string) => {
    setAmountInfo({
      base,
      fee,
      total,
      currency: Currency.RUB,
      currencyCode: 'RUB',
    })
  }

  const getAmount = (amount: number) => {
    if (paymentMethod === PaymentMethod.CARD) {
      const numericAmount = Number(amount)

      // нужно для того, чтобы выровнять стоимость номинала для карты в сравнении с сбп/лс
      return numericAmount + getCardAdditionalAmount(numericAmount, commissionData, payee, isVouchers)
    }

    return amount
  }

  const getPanNumber = () => {
    if (paymentMethod === PaymentMethod.SBP) {
      return MOCK_PAN_NUMBER
    }

    if (paymentMethod === PaymentMethod.CARD || !isAuth) {
      const cardNumber = (paymentFormRef.current?.values?.cardNumber || '').split(' ').join('')

      return cardNumber.length === CARD_NUMBER_LENGTH ? cardNumber : MOCK_PAN_NUMBER
    }

    return undefined
  }

  const getExpiryDate = () => {
    if (paymentMethod === PaymentMethod.CARD) {
      if (!paymentFormRef.current?.values?.expiryDate) {
        return undefined
      }

      const [month, year] = paymentFormRef.current?.values?.expiryDate?.split('/') || ''

      return `20${year}${month}`
    }

    return undefined
  }

  const getCvc = () => {
    if (paymentMethod === PaymentMethod.CARD) {
      return paymentFormRef.current?.values?.cvc
    }

    return undefined
  }

  const getCurrentBindingOnRequest = (bindings: Binding[]) => {
    const anonymousCardBinding = bindings?.find(
      (binding) => binding.bindingType === PayeeBindingTypesEnum.ANONYMOUSCARD
    )

    if (paymentMethod === PaymentMethod.SBP || !isAuth) {
      return anonymousCardBinding
    }

    if (paymentMethod === PaymentMethod.CARD) {
      const boundCardBinding = bindings?.find(
        (binding) =>
          binding.bindingType === 'BOUND_CARD' &&
          isCardMatch(binding.maskedPan, paymentFormRef.current?.values?.cardNumber as string)
      )

      return boundCardBinding ?? anonymousCardBinding
    }

    const mtsAccountBinding = bindings?.find((binding) => binding.bindingType === PayeeBindingTypesEnum.MTSACCOUNT)

    return mtsAccountBinding
  }

  const getCurrentBindingOnResponse = (bindings: EWalletBinding[], currentBindingId?: string) => {
    const anonymousCardBinding = bindings?.find((binding) => binding.bindingId === PayeeBindingTypesEnum.ANONYMOUSCARD)

    if (paymentMethod === PaymentMethod.SBP || !isAuth) {
      return anonymousCardBinding
    }

    const boundCardBinding = bindings?.find((binding) => binding.bindingId === currentBindingId)

    if (paymentMethod === PaymentMethod.CARD) {
      return boundCardBinding ?? anonymousCardBinding
    }

    return boundCardBinding
  }

  const getPCEKCommissionData = (amount: Required<Pick<Amount, 'base' | 'fee' | 'total'>>) => {
    const currentBindingId = getCurrentBindingOnRequest(bindings)?.bindingId

    const isPaymentDisabled =
      isAuth && paymentMethod === PaymentMethod.MTS_ACCOUNT && (!isAuth || !currentBindingId || !operatorMTS)
    const values = paymentFormRef?.current?.values

    if (isPaymentDisabled || !values) {
      return
    }

    const actualService = getActualServiceForCommision({
      isSteam,
      isVouchers,
      payee,
      activeDenomination,
      paymentMethod,
    })

    if (!actualService) {
      return
    }

    const { serviceId } = actualService

    const isGameKey = checkIsGameKey(payee)

    const serviceParams = getPcekServiceParamsByTspUrl({
      values,
      isVoucher: isVouchers,
      isGameKey,
      url: payee.features?.tsp_url ?? '',
      fields: getVisibleTspFields(payee),
    })

    const onSuccess = (data: TransferTermsRs) => {
      const { bindings: responseBindings } = data

      const currentBinding = getCurrentBindingOnResponse(responseBindings, currentBindingId)

      setPcekMdOrder(currentBinding?.mdOrder || '')

      const { base } = parseAmountInfo(currentBinding?.amount)

      if (base === 0) {
        updateAmountInfo('0', '0', '0')
      } else {
        updateAmountInfo(amount.base, amount.total, amount.fee)
      }
    }

    const onError = () => {
      toast({ variant: 'error', description: MESSAGE_COMMISSION })
    }

    getPaymentTerms({
      amount: getAmount(Number(amount.base)),
      pan: getPanNumber(),
      expiry: getExpiryDate(),
      cvc: getCvc(),
      serviceParams,
      currency: Currency.RUB,
      serviceId: isPromocodeActivatedRef.current && isSteam ? '24916' : serviceId || '',
      bindingId: currentBindingId,
      onSuccess,
      onError,
    })
  }

  const debouncedGetPCEKCommissionData = useCallback(debounce(getPCEKCommissionData, 500), [
    isAuth,
    bindings,
    operatorMTS,
    isSteam,
    isVouchers,
    payee,
    activeDenomination,
    isPromocodeActivated,
    paymentMethod,
  ])

  const getCommissions = (amount: Required<Pick<Amount, 'base' | 'fee' | 'total'>>) => {
    if (paymentMethod === PaymentMethod.SBP) {
      updateAmountInfo(amount.base, amount.total, amount.fee)
      // if (isSteam && !isVouchers) {
      //   updateAmountInfo(String(base), String(base + Number('50')), '50')
      // } else {
      //   getPCEKCommissionData({ amount: base })
      // }
    } else if (isSteam) {
      debouncedGetPCEKCommissionData(amount)
    } else {
      getPCEKCommissionData(amount)
    }
  }

  return { getCommissions, isGetPaymentTermsLoading, cancelGetPaymentTermsRequest }
}
